import React, { Fragment, useEffect, useState } from "react";
import { compose } from "redux";
import { useLocation, withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import cn from "clsx";

import { HELP_DATA_TILES, NEED_HELP_TOPIC_MAP } from "@/constants";
import { getNeedHelpData } from "@/redux/actions/needHelp.action";
import { useAppSelector } from "@/redux/store";
import { withLanguageFromUrl } from "@/hocs";
import { withPageInitialLoad } from "@/libs/pageDataBridge";
import { useIsRTL } from "@/hooks";
import Page from "@/components/page";
import Breadcrumbs from "@/components/breadcrumbs";
import NeedHelpCategories from "./helpCategoriesMob";
import NeedHelpFooter from "./footer";
import NeedHelpHeader from "./header";
import HelpAccordions from "./accordions";
import HelpDetailsTopicsSection from "./helpDetailsTopics";
import "./style.scss";

const HelpDetails = ({ history }) => {
  const [accordionTitle, setAccordionTitle] = useState(
    "frequentlyAskedQuestions"
  );
  const [contentObject, setContentObject] = useState<{
    content: any;
    meta: any;
  }>({ content: undefined, meta: undefined });

  const { t } = useTranslation(["needHelp", "seo", "breadcrumbs"]);

  const isRTL = useIsRTL();
  const location = useLocation();

  const needHelpContent = useAppSelector(
    state => state.needHelpReducer.needHelpContent
  );

  const breadcrumbsList = [
    {
      defaultValue: t("breadcrumbs:home", { lng: "en" }),
      link: "/",
      value: t("breadcrumbs:home")
    },
    {
      defaultValue: t("needHelp:needHelp", { lng: "en" }),
      link: "/need-help/",
      value: t("needHelp:needHelp")
    },
    {
      defaultValue: t(`needHelp:${accordionTitle}`, { lng: "en" }),
      link: "/",
      value: t(`needHelp:${accordionTitle}`)
    }
  ];

  useEffect(() => {
    const topic = Object.keys(NEED_HELP_TOPIC_MAP).find(topic =>
      location.pathname.includes(topic)
    );

    if (topic) {
      const newTitle = NEED_HELP_TOPIC_MAP[topic];
      setContentObject(needHelpContent[newTitle]);

      if (accordionTitle !== newTitle) {
        setAccordionTitle(newTitle);
      }
    } else {
      history.push("/need-help/");
    }
  }, [location, isRTL, needHelpContent]);

  const accordionContent =
    contentObject && contentObject.content ? contentObject.content : [];
  const meta = contentObject && contentObject?.meta;
  const metaDescription = meta?.metaDescription.trim();
  const metaTitle = meta?.title && `${meta.title.trim()} | ${"seo:bfl"}`;

  return (
    <Page
      title={metaTitle}
      description={metaDescription}
      id="need-help/order-issues"
    >
      <Fragment>
        <Breadcrumbs breadcrumbs={breadcrumbsList} />
        <div className={cn("help_container container", { arabic: isRTL })}>
          <NeedHelpHeader />
          <div className="qa_container">
            <HelpAccordions
              accordionData={accordionContent}
              title={accordionTitle}
            />
            <HelpDetailsTopicsSection />
            <NeedHelpCategories />
          </div>
          <NeedHelpFooter helpDataTiles={HELP_DATA_TILES} />
        </div>
      </Fragment>
    </Page>
  );
};

export default withPageInitialLoad({
  pageName: "help-details",
  page: compose(withRouter, withLanguageFromUrl)(HelpDetails),
  strategy: async ({ store }) => {
    const { common } = store.getState();
    const { settings } = common;
    await store.dispatch(getNeedHelpData(settings.countryId));
  },
  ssr: true,
  loader: true
});
